import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, BrowserRouter } from 'react-router'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import theme from '@/shared/theme/theme'
import { useAuthStore, ProtectedRoute } from '@/features/auth'
import Layout from '@/shared/components/Layout/Layout'
import NotFoundPage from '@/shared/components/NotFoundPage'
import { ErrorBoundary, Toast } from '@/shared/components/ui'
import { useToastStore } from '@/shared/store/toastStore'
import { extractApiError } from '@/shared/lib/api'

const DashboardPage    = lazy(() => import('@/features/dashboard/components/DashboardPage'))
const TransactionsPage = lazy(() => import('@/features/transactions/components/TransactionsPage'))
const ImportPage       = lazy(() => import('@/features/import/components/ImportPage'))
const CategoriesPage   = lazy(() => import('@/features/categories/components/CategoriesPage'))
const RulesPage        = lazy(() => import('@/features/rules/components/RulesPage'))
const SettingsPage     = lazy(() => import('@/features/settings/components/SettingsPage'))

const PageLoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
    </Box>
)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
            // Single-household app: there is no concurrent writer to sync with,
            // so a focus refetch has no upside. It does have a downside —
            // alt-tabbing to a bank statement and back would reload the whole
            // transaction list under an active keyboard cursor.
            refetchOnWindowFocus: false,
            // Left at the default on purpose: structural sharing is what keeps
            // row object identity stable across refetches, which is the
            // precondition for React.memo on the table rows doing anything.
            // structuralSharing: true
        },
    },
    queryCache: new QueryCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
    mutationCache: new MutationCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
})

const App = () => {
    const { initializeAuth } = useAuthStore()

    useEffect(() => {
        void initializeAuth()
    }, [initializeAuth])

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <ProtectedRoute>
                        <ErrorBoundary>
                            <Suspense fallback={<PageLoadingFallback />}>
                                <Routes>
                                    <Route path="/" element={<Layout />}>
                                        <Route index element={<DashboardPage />} />
                                        <Route path="transactions" element={<TransactionsPage />} />
                                        <Route path="import" element={<ImportPage />} />
                                        <Route path="categories" element={<CategoriesPage />} />
                                        <Route path="rules" element={<RulesPage />} />
                                        <Route path="settings" element={<SettingsPage />} />
                                    </Route>
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                        </ErrorBoundary>
                    </ProtectedRoute>
                    <Toast />
                </ThemeProvider>
            </BrowserRouter>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    )
}

export default App
